import { Google, Telegram } from "@mui/icons-material";
import { Box, Button, Typography, useMediaQuery, useTheme, Tooltip } from "@mui/material";
import { MessageSquareIcon, MailIcon, InstagramIcon, InboxIcon, Linkedin } from "lucide-react";
import React from "react";

interface SidebarProps {
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
}

const SearchBar: React.FC<SidebarProps> = ({ activeFilter, setActiveFilter }) => {
  const theme = useTheme();
  const isTablet = useMediaQuery("(max-width:1280px)");
  const isMobile = useMediaQuery("(max-width:768px)");

  const channels = [
    {
      id: "all",
      name: "All",
      longName: "Tutti i Messaggi",
      icon: <InboxIcon size={18} />,
      comingSoon: false,
    },
    {
      id: "whatsapp",
      name: "WhatsApp",
      longName: "WhatsApp",
      icon: <MessageSquareIcon size={18} />,
      comingSoon: false,
    },
    {
      id: "email",
      name: "Email",
      longName: "Email",
      icon: <MailIcon size={18} />,
      comingSoon: true,
    },
    {
      id: "instagram",
      name: "Instagram",
      longName: "Instagram DM",
      icon: <InstagramIcon size={18} />,
      comingSoon: false,
    },
    {
      id: "facebook",
      name: "Facebook",
      longName: "Facebook Messenger",
      icon: <MessageSquareIcon size={18} />,
      comingSoon: true,
    },
    {
      id: "linkedin",
      name: "Linkedin",
      longName: "Linkedin",
      icon: <Linkedin size={18} />,
      comingSoon: true,
    },
    {
      id: "telegram",
      name: "Telegram",
      longName: "Telegram",
      icon: <Telegram />,
      comingSoon: true,
    },
    {
      id: "tiktok",
      name: "TikTok",
      longName: "TikTok",
      icon: <MessageSquareIcon size={18} />,
      comingSoon: true,
    },
    {
      id: "gmb",
      name: "Google My Business",
      longName: "Google My Business",
      icon: <Google />,
      comingSoon: false,
    },
  ];

  // Mappa dei colori di hover per ciascun canale
  const hoverColors: Record<string, string> = {
    whatsapp: "#25D366",
    email: "#EA4335",
    instagram: "#E1306C",
    facebook: "#1877F2",
    linkedin: "#0077B5",
    telegram: "#0088CC",
    tiktok: "#69C9D0",
    all: "#F9FAFB",
    GMB: "#4285F4",
  };

  return (
    <Box
      sx={{
        height: { xs: 56, sm: 64 },
        backgroundColor: "#F9FAFB",
        color: "#111827",
        width: "100%",
        display: "flex",
        alignItems: "center",
        px: { xs: 1.5, sm: 2 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: { xs: "2px", sm: "4px" },
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          paddingTop: "10px",
        }}
      >
        {channels.map((channel) => (
          <Tooltip
            key={channel.id}
            title={channel.comingSoon ? "Coming soon!" : ""}
            arrow
            placement="top"
          >
            <span>
              {" "}
              {/* Wrapper needed for disabled buttons with tooltips */}
              <Button
                onClick={() => !channel.comingSoon && setActiveFilter(channel.id)}
                disabled={channel.comingSoon}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textTransform: "none",
                  paddingX: { xs: 1, sm: 1.5 },
                  paddingY: "6px",
                  minHeight: "36px",
                  minWidth: isMobile ? "40px" : "auto",
                  borderRadius: 1,
                  whiteSpace: "nowrap",
                  transition: "background-color 150ms, color 150ms",
                  backgroundColor:
                    activeFilter === channel.id ? hoverColors[channel.id] : "transparent",
                  color: channel.comingSoon
                    ? "rgba(0, 0, 0, 0.38)"
                    : activeFilter === channel.id
                    ? "#fff"
                    : "#111827",
                  "&:hover": {
                    backgroundColor: channel.comingSoon ? "transparent" : hoverColors[channel.id],
                    color: channel.comingSoon ? "rgba(0, 0, 0, 0.38)" : "#fff",
                  },
                  flexShrink: 0,
                  // Add a subtle indication for coming soon items
                  ...(channel.comingSoon && {
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 3,
                      top: 3,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.warning.main,
                    },
                  }),
                }}
              >
                <Box
                  sx={{
                    mr: { xs: 0.5, sm: 1 },
                    display: "flex",
                    opacity: channel.comingSoon ? 0.5 : 1,
                  }}
                >
                  {channel.icon}
                </Box>

                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: { sm: "0.8rem", md: "0.875rem" },
                    opacity: channel.comingSoon ? 0.5 : 1,
                  }}
                >
                  {isTablet ? channel.name : channel.longName}
                </Typography>
              </Button>
            </span>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default SearchBar;
